import { useSyncExternalStore } from 'react';

/*
 * Display currency. Every price in the app is in INR — that is what bookings are charged in.
 * The customer's chosen currency only changes how prices are shown: formatPrice() converts with
 * the admin-set rate from /api/currencies. Amounts that are actually charged or refunded use
 * formatINR(); the checkout shows the INR amount next to the converted one.
 */

export const BASE_CURRENCY = 'INR';
const STORAGE_KEY = 'tripfresh.currency';
const base = { code: BASE_CURRENCY, name: 'Indian Rupee', rate: 1 };

const readStored = () => {
  try { return window.localStorage.getItem(STORAGE_KEY) || BASE_CURRENCY; } catch { return BASE_CURRENCY; }
};

let state = { currencies: [base], code: readStored() };
const listeners = new Set();
const emit = () => listeners.forEach((listener) => listener());

export const subscribeCurrency = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const getCurrencyState = () => state;

/** Re-renders the caller when the selected currency or the rates change. */
export const useCurrencyState = () => useSyncExternalStore(subscribeCurrency, getCurrencyState);

export function setCurrencies(list) {
  const currencies = list.some((c) => c.code === BASE_CURRENCY) ? list : [base, ...list];
  state = { ...state, currencies };
  emit();
}

export function selectCurrency(code) {
  try { window.localStorage.setItem(STORAGE_KEY, code); } catch { /* storage unavailable */ }
  if (code === state.code) return;
  state = { ...state, code };
  emit();
}

/** The selected currency, or INR while rates are loading or if it is no longer offered. */
export const activeCurrency = () => state.currencies.find((c) => c.code === state.code) || base;

export const formatINR = (n) => '₹' + Math.round(n).toLocaleString('en-IN');

/** Formats an amount already in `code`. Large amounts drop the minor units, like the INR prices. */
export const formatMoney = (amount, code) => (code === BASE_CURRENCY
  ? formatINR(amount)
  : new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: code,
    ...(Math.abs(amount) >= 100 ? { maximumFractionDigits: 0, minimumFractionDigits: 0 } : {}),
  }).format(amount));

/** An INR amount shown in the customer's currency. */
export const formatPrice = (inr) => {
  const currency = activeCurrency();
  return formatMoney(inr * currency.rate, currency.code);
};

/** True when prices are shown converted, so the checkout should also show the INR charge. */
export const isConverted = () => activeCurrency().code !== BASE_CURRENCY;

/** An amount charged or refunded in INR, with the converted amount when another currency is selected. */
export const formatCharge = (inr) => (isConverted() ? `${formatINR(inr)} (≈ ${formatPrice(inr)})` : formatINR(inr));
